import React, { useEffect, useState } from 'react'
import { inject, observer } from 'mobx-react'
import { Row, Col, Button, Form, Alert } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import LoadingSpinnerAlt from 'Components/LoadingSpinnerAlt'
import DatePicker from 'Components/DatePicker'
import uiStore from 'stores/uiStore'
import Utilities from 'Utilities'
import { currentMonthCivilRange, parseApiCivilDate, toApiCivilDate, todayCivil } from 'Utilities/dateTime'

const GenerateButton = ({ txtStore: store }) => {
  const { t } = useTranslation()
  const [txtDate, setTxtDate] = useState(() => parseApiCivilDate(todayCivil()) || new Date())

  useEffect(() => {
    store.fetchLastTxtDate().then(() => {
      store.fetchPendingForTxt(txtDate)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleDateChange(value) {
    const next = Array.isArray(value) ? value[0] : value
    setTxtDate(next)
    if (next) store.fetchPendingForTxt(next)
  }

  async function handleGenerateClick() {
    const dateStr = toApiCivilDate(txtDate)
    if (!dateStr) {
      uiStore.notifyWarning(t('txt_date_required'))
      return
    }

    if (store.lastTxtDate && dateStr <= store.lastTxtDate) {
      uiStore.notifyWarning(t('txt_date_must_be_after_last'))
      return
    }

    if (store.pendingCount === 0) {
      uiStore.notifyWarning(t('no_pending_withholdings_for_txt'))
      return
    }

    const resp = await uiStore.confirm({
      message: t('really_want_to_generate_txt_with_date', {
        date: Utilities.TransformDate(dateStr),
        count: store.pendingCount ?? 0,
      }),
      confirmLabel: t('generate'),
      cancelLabel: t('cancel'),
      variant: 'primary',
    })
    if (!resp) return

    try {
      const ok = await store.createTxt(dateStr)
      if (ok) {
        uiStore.notifySuccess(t('txt_generated_successfully'))
        await store.fetchLastTxtDate()
        await store.fetchPendingForTxt(dateStr)
        const range = currentMonthCivilRange()
        store.fetchTxts(range.from, range.to)
      }
    } catch (err) {
      const message = err?.response?.data?.message || t('error_saving_data')
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text()
          const parsed = JSON.parse(text)
          uiStore.notifyError(parsed.message || message)
          return
        } catch {
          /* ignore */
        }
      }
      uiStore.notifyError(message)
    }
  }

  return (
    <div className="border rounded p-3 bg-light">
      <h5 className="mb-3">{t('generate_withholding_txt')}</h5>
      <Row className="align-items-end">
        <Col xs={12} md={4} className="mb-2">
          <Form.Group controlId="txtDate" className="mb-0">
            <Form.Label>{t('txt_date')}</Form.Label>
            <DatePicker value={txtDate} onChange={handleDateChange} />
          </Form.Group>
        </Col>
        <Col xs={12} md={5} className="mb-2">
          <div className="small text-muted">
            <div>
              <strong>{t('last_txt_date')}: </strong>
              {store.lastTxtDate
                ? Utilities.TransformDate(store.lastTxtDate)
                : t('no_previous_txt')}
            </div>
            {store.pendingCount != null && (
              <div className="mt-1">
                <strong>{t('pending_withholdings_for_txt')}: </strong>
                {store.pendingCount}
                {store.pendingFrom && store.pendingTo && (
                  <span>
                    {' '}
                    ({Utilities.TransformDate(store.pendingFrom)} — {Utilities.TransformDate(store.pendingTo)})
                  </span>
                )}
              </div>
            )}
          </div>
        </Col>
        <Col xs={12} md={3} className="mb-2 d-flex justify-content-md-end">
          {store.isGenerating && <LoadingSpinnerAlt />}
          {!store.isGenerating && (
            <Button className="w-100" onClick={handleGenerateClick}>
              {t('generate')}
            </Button>
          )}
        </Col>
      </Row>
      <Alert variant="secondary" className="mb-0 mt-3 py-2">
        {t('txt_generation_hint')}
      </Alert>
    </div>
  )
}

export default inject('txtStore')(observer(GenerateButton))
